import { t } from "i18next";
import { useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import { Trash2 } from "lucide-react";

import { FLASHPAY_ROLES, hasRole } from "@/core/auth";
import { RowActionsMenu } from "@/core/components/shared/data-table";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useDeleteProductProviderMapping } from "@/features/ledger/api/use-delete-product-provider-mapping";
import type { ProductProviderMappingRow } from "@/features/ledger/components/product-provider-mappings/product-provider-mappings-columns";

/**
 * Menu d'actions de ligne des associations code produit / provider.
 * La désactivation (`DELETE`) est réservée à flashpay-superadmin
 * (masquage — le backend reste l'autorité finale).
 */
export function ProductProviderMappingRowActions({
  mapping,
  navigate: _navigate,
}: {
  mapping: ProductProviderMappingRow;
  navigate: NavigateFunction;
}) {
  const remove = useDeleteProductProviderMapping();
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (!hasRole(FLASHPAY_ROLES.SUPERADMIN)) return null;

  return (
    <>
      <RowActionsMenu
        actions={[
          {
            id: "delete",
            label: t("ledger.mappings.actions.delete"),
            icon: Trash2,
            variant: "destructive",
            onClick: () => setDeleteOpen(true),
          },
        ]}
      />
      <MutationConfirmPreview
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        data={{
          id: mapping.id,
          productCodeId: mapping.productCodeId,
          providerId: mapping.providerId,
        }}
        title={t("common.mutationPreview.deleteTitle")}
        description={t("common.mutationPreview.deleteDescription")}
        confirmLabel={t("ledger.mappings.actions.delete")}
        t={t}
        pending={remove.isPending}
        onConfirm={() =>
          remove.mutate(mapping.id, {
            onSuccess: () => {
              notifSilence(t("ledger.mappings.feedback.deleted"));
              setDeleteOpen(false);
            },
            onError: (error) => notifSilenceError(error.message),
          })
        }
      />
    </>
  );
}
